import React, { useEffect, useState, useRef } from "react";
import { MdSearch, MdExpandMore, MdExpandLess, MdRestaurantMenu, MdAccessTime } from "react-icons/md";
import Card from "components/card";
import { useAuth } from "contexts/AuthContext";
import { getMenus, getCategories, getProducts, setProductAvailability } from "services/menuService";

function fmtTime(ts) {
  if (!ts) return null;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
}

// Minutes from now until a given hour today / tomorrow (null if already past today).
function minutesUntilToday(hour) {
  const now = new Date();
  const t = new Date();
  t.setHours(hour, 0, 0, 0);
  const diff = Math.round((t - now) / 60000);
  return diff > 0 ? diff : null;
}
function minutesUntilTomorrow(hour) {
  const now = new Date();
  const t = new Date();
  t.setDate(t.getDate() + 1);
  t.setHours(hour, 0, 0, 0);
  return Math.round((t - now) / 60000);
}

function ProductRow({ product, onSetAvailability }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const available = product.available === 1 || product.available === true;
  const backAt = !available && product.unavailable_until ? fmtTime(product.unavailable_until) : null;

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const options = [
    { label: "1 heure", minutes: 60 },
    { label: "2 heures", minutes: 120 },
    { label: "Ce soir (23:00)", minutes: minutesUntilToday(23) },
    { label: "Demain matin (09:00)", minutes: minutesUntilTomorrow(9) },
    { label: "Jusqu'à réactivation", minutes: 0 },
  ].filter((o) => o.minutes === 0 || (o.minutes && o.minutes > 0));

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-gray-50 dark:border-navy-700 last:border-0">
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${available ? "text-navy-700 dark:text-white" : "text-gray-400 line-through"}`}>
          {product.name}
        </p>
        <div className="flex items-center gap-2">
          {product.price != null && (
            <p className="text-xs text-gray-400">{Number(product.price).toFixed(3)} TND</p>
          )}
          {!available && backAt && (
            <span className="flex items-center gap-1 text-xs font-medium text-orange-500">
              <MdAccessTime className="h-3 w-3" /> Revient à {backAt}
            </span>
          )}
        </div>
      </div>

      {available ? (
        <div className="relative ml-3 flex-shrink-0" ref={ref}>
          <button
            onClick={() => setOpen((o) => !o)}
            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-bold text-red-500 hover:bg-red-50 dark:border-navy-600"
          >
            Rupture ▾
          </button>
          {open && (
            <div className="absolute right-0 z-20 mt-1 w-48 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg dark:border-navy-700 dark:bg-navy-800">
              {options.map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => { setOpen(false); onSetAvailability(product.id, false, opt.minutes); }}
                  className="block w-full px-4 py-2.5 text-left text-xs font-medium text-navy-700 hover:bg-gray-50 dark:text-white dark:hover:bg-navy-700"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => onSetAvailability(product.id, true)}
          className="ml-3 flex-shrink-0 rounded-lg bg-green-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-600"
        >
          Réactiver
        </button>
      )}
    </div>
  );
}

function CategorySection({ category, search, onSetAvailability }) {
  const [open, setOpen] = useState(true);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getProducts(category.id)
      .then((d) => {
        if (Array.isArray(d))            setProducts(d);
        else if (Array.isArray(d?.data)) setProducts(d.data);
        else if (d?.id)                  setProducts([d]);
        else                             setProducts([]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category.id]);

  const filtered = search
    ? products.filter((p) => p.name?.toLowerCase().includes(search.toLowerCase()))
    : products;

  const availableCount = products.filter((p) => p.available === 1 || p.available === true).length;

  return (
    <div className="mb-4 rounded-xl border border-gray-100 dark:border-navy-700 overflow-hidden">
      <button
        className="flex w-full items-center justify-between bg-gray-50 dark:bg-navy-700 px-4 py-3"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          <span className="font-bold text-navy-700 dark:text-white text-sm">{category.name}</span>
          <span className="rounded-full bg-brand-100 text-brand-600 text-xs px-2 py-0.5">
            {availableCount}/{products.length}
          </span>
        </div>
        {open ? <MdExpandLess className="h-5 w-5 text-gray-400" /> : <MdExpandMore className="h-5 w-5 text-gray-400" />}
      </button>

      {open && (
        <div className="px-4 bg-white dark:bg-navy-800">
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-xs text-gray-400 py-4">Aucun produit</p>
          ) : (
            filtered.map((p) => (
              <ProductRow key={p.id} product={p} onSetAvailability={(id, available, minutes) => {
                setProducts((prev) => prev.map((pp) => pp.id === id ? {
                  ...pp,
                  available: available ? 1 : 0,
                  unavailable_until: available ? null : (minutes ? new Date(Date.now() + minutes * 60000).toISOString() : null),
                } : pp));
                onSetAvailability(id, available, minutes);
              }} />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function Menu() {
  const { restaurantId } = useAuth();
  const [menus, setMenus] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!restaurantId) return;
    getMenus(restaurantId)
      .then((d) => {
        // API may return a single object {id:65}, an array, or {data:[...]}
        let list;
        if (Array.isArray(d))            list = d;
        else if (Array.isArray(d?.data)) list = d.data;
        else if (d?.id)                  list = [d];        // single menu object
        else if (d?.data?.id)            list = [d.data];
        else                             list = [];
        setMenus(list);
        if (list.length) setSelectedMenu(list[0]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(() => {
    if (!selectedMenu) return;
    setLoading(true);
    getCategories(selectedMenu.id)
      .then((d) => {
        // Same normalisation for categories response
        if (Array.isArray(d))            setCategories(d);
        else if (Array.isArray(d?.data)) setCategories(d.data);
        else if (d?.id)                  setCategories([d]);
        else                             setCategories([]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedMenu]);

  const handleSetAvailability = async (productId, available, minutes) => {
    try {
      await setProductAvailability(productId, available, minutes);
      showToast(available ? "Produit réactivé" : "Produit mis en rupture");
    } catch {
      showToast("Erreur lors de la mise à jour", "error");
    }
  };

  const handleBulk = async (enableAll) => {
    if (!categories.length) return;
    setBulkLoading(true);
    try {
      showToast(enableAll ? "Tous les produits activés" : "Tous les produits désactivés");
    } catch {
      showToast("Erreur", "error");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <div className="mt-3">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-lg ${
          toast.type === "error" ? "bg-red-500" : "bg-green-500"
        }`}>
          {toast.msg}
        </div>
      )}

      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
          <MdRestaurantMenu className="h-7 w-7 text-brand-500" />
          Menu
        </h2>
        <div className="flex gap-2">
          <button
            className="rounded-xl bg-green-500 px-3 py-2 text-xs font-semibold text-white hover:bg-green-600 disabled:opacity-50"
            disabled={bulkLoading}
            onClick={() => handleBulk(true)}
          >
            Tout activer
          </button>
          <button
            className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            disabled={bulkLoading}
            onClick={() => handleBulk(false)}
          >
            Tout désactiver
          </button>
        </div>
      </div>

      {/* Menu selector */}
      {menus.length > 1 && (
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {menus.map((m) => (
            <button
              key={m.id}
              className={`flex-shrink-0 rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                selectedMenu?.id === m.id
                  ? "bg-brand-500 text-white"
                  : "bg-white dark:bg-navy-800 text-gray-600 dark:text-white border border-gray-200 dark:border-navy-700"
              }`}
              onClick={() => setSelectedMenu(m)}
            >
              {m.name}
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Rechercher un produit..."
          className="w-full rounded-xl border border-gray-200 dark:border-navy-600 bg-white dark:bg-navy-800 pl-10 pr-4 py-2.5 text-sm text-navy-700 dark:text-white"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : categories.length === 0 ? (
        <Card extra="p-8 text-center">
          <p className="text-gray-400">Aucune catégorie trouvée</p>
        </Card>
      ) : (
        categories.map((cat) => (
          <CategorySection
            key={cat.id}
            category={cat}
            search={search}
            onSetAvailability={handleSetAvailability}
          />
        ))
      )}
    </div>
  );
}
